import { z } from 'zod';
import { nullablePositiveIdSchema, positiveIdSchema, withAtLeastOneField, } from '../../shared/index.js';
export { hasMinutesMotionText, isBareMinutesMotion, isConsentOutcomeMotion, isUnrecordedSeedMotion, } from './consent-outcome.js';
export { isRecordedMotion, isRecordedNote, isRecordedRollCall, isRecordedVote, isStagedMotion, isStagedNote, isStagedRollCall, isStagedVote, isVoteInHistory, resolveHistoryOccurredAt, resolveVoteHistoryOccurredAt, } from './recorded-state.js';
export { computeMotionResultStatusId, computeVoteOutcome, isResolvedWithoutVote, resolveVoteSlotChange, resultStatusIdForOutcome, } from './vote-outcome.js';
export { getVotingStatus, isVotingOpen, MotionVotingStatus, pickActiveMotionId, } from './motion-voting.js';
export { MinutesChatMotionKind, MinutesChatPreset, MinutesChatTranscriptScope, minutesChatAvailabilitySchema, minutesChatContextSchema, minutesChatMessageSchema, minutesChatModelMotionSuggestionSchema, minutesChatMotionKindSchema, minutesChatMotionSuggestionSchema, minutesChatNoteSuggestionSchema, minutesChatPresetSchema, minutesChatRequestSchema, minutesChatResponseSchema, minutesChatSuggestionSchema, minutesChatTimestampSecondsSchema, minutesChatTranscriptScopeSchema, resolveMinutesChatMotionText, stripMinutesChatMotionAssistantPreamble, } from './chat.js';
export const MinutesStatus = {
    approved: 'approved',
    draft: 'draft',
    scheduled: 'scheduled',
};
export const VoteType = {
    absent: 'absent',
    abstain: 'abstain',
    aye: 'aye',
    nay: 'nay',
    recuse: 'recuse',
};
const VOTE_TYPE_VALUES = new Set(Object.values(VoteType));
const VOTE_TYPE_ALIASES = {
    affirmative: VoteType.aye,
    against: VoteType.nay,
    favor: VoteType.aye,
    for: VoteType.aye,
    'in favor': VoteType.aye,
    negative: VoteType.nay,
    no: VoteType.nay,
    opposed: VoteType.nay,
    yea: VoteType.aye,
    yay: VoteType.aye,
    yeah: VoteType.aye,
    yes: VoteType.aye,
};
/**
 * Coerces a raw vote string to a canonical VoteType, or null when unrecognized.
 */
export const normalizeVoteType = (value) => {
    if (value == null) {
        return null;
    }
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
        return null;
    }
    if (VOTE_TYPE_VALUES.has(normalized)) {
        return normalized;
    }
    return VOTE_TYPE_ALIASES[normalized] ?? null;
};
const minutesStatusSchema = z.enum(MinutesStatus);
const voteTypeSchema = z.enum(VoteType);
export const meetingMinutesCreateSchema = z.object({
    meetingBodyId: positiveIdSchema
        .optional()
        .describe('Target meeting body. Omit to create one MeetingMinutes record for every meeting body on the event in a single transaction.'),
    status: minutesStatusSchema.default('draft'),
});
export const meetingMinutesUpdateSchema = withAtLeastOneField(z.object({
    status: minutesStatusSchema.optional(),
    publishedAt: z.iso.datetime().nullable().optional(),
}), ['status', 'publishedAt']).describe('Updates minutes approval/publishing state; requires at least one mutable field.');
export const minutesEntryUpdateSchema = withAtLeastOneField(z.object({
    title: z.string().min(1, 'Title is required').max(500).optional(),
    itemNumber: z.string().max(50).nullable().optional(),
}), ['title', 'itemNumber']).describe('Updates a minutes entry. Only the title and item number are mutable today; ordering and parent links are managed separately. Title cannot be cleared once set.');
export const minutesNoteCreateSchema = z.object({
    minutesEntryId: positiveIdSchema,
    content: z.string().min(1, 'Content is required'),
    isPrivate: z.boolean().default(false),
    orderById: z.number().int().min(1).default(1),
    recorded: z
        .boolean()
        .optional()
        .describe('True for live-console origin (stamps recordedAt); omitted leaves the note staged.'),
});
export const minutesNoteUpdateSchema = withAtLeastOneField(z.object({
    content: z.string().min(1, 'Content is required').optional(),
    isPrivate: z.boolean().optional(),
    orderById: z.number().int().min(1).optional(),
    recorded: z
        .boolean()
        .optional()
        .describe('True records a staged note (stamps recordedAt once); idempotent.'),
}), ['content', 'isPrivate', 'orderById', 'recorded']);
export const minutesMotionCreateSchema = z
    .object({
    minutesEntryId: nullablePositiveIdSchema
        .optional()
        .describe('Null/omitted for privileged motions that are not tied to an agenda entry.'),
    legislativeActionId: positiveIdSchema,
    motionText: z.string().min(1).max(10_000).optional(),
    moverId: nullablePositiveIdSchema
        .optional()
        .describe('Mover meeting member. Nullable for placeholder motions seeded before the meeting (e.g. recommended/default actions).'),
    seconderId: nullablePositiveIdSchema.optional(),
    referredMeetingBodyId: nullablePositiveIdSchema
        .optional()
        .describe('Meeting body the item is referred/reassigned to. Required when the legislative action has requiresMeetingBodyReassignment; ignored (normalized to null) otherwise.'),
    parentMotionId: nullablePositiveIdSchema.optional(),
    resultLegislativeStatusId: nullablePositiveIdSchema
        .optional()
        .describe('Status outcome captured after vote tally; null for pending/non-status motions.'),
    recorded: z
        .boolean()
        .optional()
        .describe('True for live-console origin (stamps recordedAt); omitted leaves the motion staged.'),
})
    .describe('Creates a minutes motion record for main, subsidiary, or privileged motions.');
export const minutesMotionUpdateSchema = withAtLeastOneField(z.object({
    minutesEntryId: nullablePositiveIdSchema.optional(),
    legislativeActionId: positiveIdSchema.optional(),
    motionText: z.string().min(1).max(10_000).nullable().optional(),
    moverId: nullablePositiveIdSchema.optional(),
    seconderId: nullablePositiveIdSchema.optional(),
    referredMeetingBodyId: nullablePositiveIdSchema
        .optional()
        .describe('Meeting body the item is referred/reassigned to. Required when the (effective) legislative action has requiresMeetingBodyReassignment; normalized to null otherwise.'),
    parentMotionId: nullablePositiveIdSchema.optional(),
    resultLegislativeStatusId: nullablePositiveIdSchema.optional(),
    recorded: z
        .boolean()
        .optional()
        .describe('True records a staged motion (stamps recordedAt once); idempotent.'),
}), [
    'minutesEntryId',
    'legislativeActionId',
    'motionText',
    'moverId',
    'seconderId',
    'referredMeetingBodyId',
    'parentMotionId',
    'resultLegislativeStatusId',
    'recorded',
]).describe('Updates an existing minutes motion; requires at least one mutable field.');
export const minutesMotionVoteUpsertSchema = z.object({
    meetingMemberId: positiveIdSchema,
    vote: voteTypeSchema,
});
export const minutesMotionVoteSetSchema = z.object({
    vote: voteTypeSchema.nullable(),
});
export const MemberVoteType = {
    abstain: 'abstain',
    aye: 'aye',
    nay: 'nay',
    recuse: 'recuse',
};
const memberVoteTypeSchema = z.enum(MemberVoteType);
export const memberVoteSchema = z
    .object({
    vote: memberVoteTypeSchema,
})
    .describe('Member self-vote on a motion. Aye, nay, abstain, or recuse.');
export const minutesMotionVotesReplaceSchema = z
    .object({
    votes: z.array(minutesMotionVoteUpsertSchema),
    recorded: z
        .boolean()
        .optional()
        .describe('True for a live-console tally (stamps voteRecordedAt); omitted leaves it staged.'),
})
    .superRefine(({ votes }, ctx) => {
    const seenMeetingMembers = new Set();
    votes.forEach(({ meetingMemberId }, index) => {
        if (seenMeetingMembers.has(meetingMemberId)) {
            ctx.addIssue({
                code: 'custom',
                path: ['votes', index, 'meetingMemberId'],
                message: 'Duplicate meetingMemberId in votes payload',
            });
        }
        seenMeetingMembers.add(meetingMemberId);
    });
})
    .describe('Replaces all votes on a motion. Each meetingMemberId must appear at most once in the payload.');
/** Reserved for live/POST pull flows (GOV-1168). DELETE pull uses `:minutesEntryId` param only. */
export const minutesConsentItemPullSchema = z.object({
    minutesEntryId: positiveIdSchema,
});
export const minutesConsentItemRestoreSchema = z.object({
    consentSectionEntryId: positiveIdSchema,
    minutesEntryId: positiveIdSchema,
});
export const ItemRecordType = {
    motion: 'motion',
    note: 'note',
    rollCall: 'rollCall',
    vote: 'vote',
};
export const minutesItemRecordsReorderSchema = z
    .object({
    items: z
        .array(z.object({
        id: positiveIdSchema,
        type: z.enum(ItemRecordType),
    }))
        .min(1),
})
    .describe('Reorders all item records within a minutes entry. Provide all items in the desired order; positions will be reassigned 1…N.');
export const minutesEntriesReorderSchema = z
    .object({
    entries: z
        .array(z.object({
        id: positiveIdSchema,
        parentMinutesEntryId: nullablePositiveIdSchema,
    }))
        .min(1),
})
    .describe('Reorders and optionally reparents minutes entries. Provide all affected entries with their new parentMinutesEntryId and desired order within sibling group; positions are reassigned 1…N per sibling group.');
const meetingBodyIdsInputSchema = z
    .array(positiveIdSchema)
    .refine((ids) => new Set(ids).size === ids.length, {
    message: 'Meeting body IDs must not contain duplicates',
});
/**
 * Shared field shape for adding an item to the minutes/floor tree. Kept as a plain object so the
 * request/form schemas below can compose it (`.omit`/`.extend`); the request-level conditional
 * rules live on {@link minutesItemCreateSchema}.
 */
const minutesItemCreateBaseSchema = z.object({
    fullTitle: z.string().optional(),
    summary: z.string().optional().nullable(),
    legislativeTypeId: z.number().optional(),
    legislativeItemId: nullablePositiveIdSchema
        .optional()
        .describe('Existing legislative item to link as a floor item. When set, fullTitle/legislativeTypeId are ignored and taken from the item.'),
    meetingBodyIds: meetingBodyIdsInputSchema.optional(),
    parentMinutesEntryId: nullablePositiveIdSchema.describe('Parent section entry id, or null to add a root-level floor item.'),
    staffContactIds: z.array(z.number().int().positive()).max(50).optional(),
    departmentId: z.number().nullable().optional(),
});
/**
 * Request schema for `POST /meetings/:id/minutes/items`. Linking an existing item derives its
 * title/type from that item; creating a brand-new floor item requires both up front.
 */
export const minutesItemCreateSchema = minutesItemCreateBaseSchema.superRefine((data, ctx) => {
    if (data.legislativeItemId != null) {
        return;
    }
    if (!data.fullTitle || data.fullTitle.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Full title is required',
            path: ['fullTitle'],
        });
    }
    if (data.legislativeTypeId == null || data.legislativeTypeId < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Legislative type is required',
            path: ['legislativeTypeId'],
        });
    }
});
/**
 * Form schema for the create-new (quick-add) surfaces. Parent placement is supplied by the view,
 * and these surfaces never link an existing item, so title/type are always required here.
 */
export const minutesItemQuickAddFormSchema = minutesItemCreateBaseSchema
    .omit({ parentMinutesEntryId: true, legislativeItemId: true })
    .extend({
    fullTitle: z.string().min(1, 'Full title is required'),
    legislativeTypeId: z.number().min(1, 'Legislative type is required'),
});
export const minutesItemDuplicateSchema = z.object({
    sourceMinutesEntryId: positiveIdSchema.describe('Minutes entry to duplicate. Must belong to this meeting, reference a legislative item, and not be removed or privileged.'),
    parentMinutesEntryId: nullablePositiveIdSchema.describe('Parent section entry id, or null to add a root-level floor item.'),
    meetingBodyIds: meetingBodyIdsInputSchema.min(1, 'At least one meeting body is required'),
});
export const minutesSectionDuplicateSchema = z.object({
    sourceMinutesEntryId: positiveIdSchema.describe('Section minutes entry to duplicate. Must belong to this meeting, link an agenda section, and not be removed or privileged.'),
});
export const minutesAssociateItemSchema = z.object({
    legislativeTypeId: positiveIdSchema,
    meetingBodyIds: meetingBodyIdsInputSchema.min(1, 'At least one meeting body is required'),
    targetMeetingId: positiveIdSchema.describe('Meeting the created item is targeted at — typically the later meeting that will approve these minutes. Its date is read from the meeting itself.'),
});
export const minutesDisassociateItemSchema = z.object({
    delete: z
        .boolean()
        .default(false)
        .describe('When true, the unlinked legislative item is also soft-deleted.'),
});
export const minutesDocumentsCreateSchema = z
    .object({
    agendaDocxTemplateId: z.number().int().positive().nullable().optional(),
    // Keeps the rendered Word file as the stored document instead of converting it to PDF.
    // Word documents can't be published to the public portal.
    docx: z.boolean().optional(),
    main: z.boolean(),
    meetingBodyIds: z
        .array(z.number().int().positive())
        .refine((ids) => new Set(ids).size === ids.length, {
        message: 'Meeting body IDs must not contain duplicates',
    })
        .default([]),
})
    .refine((data) => data.main || data.meetingBodyIds.length > 0, {
    message: 'Select at least one document to be created',
});
/**
 * Returns the stable id for an item record. For motion, note, and vote records this is the
 * underlying row id; for roll-call groups this is the lowest constituent `RollCall.id`
 * (`representativeId`) so sortable keys, reorder payloads, and insert-position lookups stay
 * deterministic regardless of how many peer rows share the slot.
 */
export const getMinutesItemRecordId = (record) => record.type === 'rollCall' ? record.record.representativeId : record.record.id;
